import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { featuredOffers, gameOffers, vpnOffers, surveyOffers, appOffers, giftCardOffers, financeOffers } from "../data/offers";
import {
  ArrowLeft,
  ShieldCheck,
  AlertCircle,
  Lock,
  Unlock,
  Clock,
  Gift,
  Sparkles,
  Zap,
  Shield,
} from "lucide-react";
import { motion } from "framer-motion";
import LockerButton from '../components/LockerButton';

const OfferBridge = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [timer, setTimer] = useState(120); // 2 minutes timer

  const allOffers = [
    ...featuredOffers,
    ...gameOffers,
    ...vpnOffers,
    ...surveyOffers,
    ...appOffers,
    ...giftCardOffers,
    ...(financeOffers || [])
  ];

  const offer = allOffers.find((o) => o.id === id) || {
    id: id,
    title: id?.replace(/-/g, " ").toUpperCase(),
    subtitle: "Complete verification to unlock your rewards.",
    image: `https://picsum.photos/seed/${id}/600/400`,
    ctaLabel: "UNLOCK YOUR REWARD",
    rewardValue: "$50",
    rewardType: "Gift Card",
    difficulty: "Easy",
    estimatedTime: "2 minutes",
  };

  const [sessionOfferName, setSessionOfferName] = useState(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('selected_offer');
      if (stored) {
        const parsed = JSON.parse(stored);
        setSessionOfferName(parsed.title || parsed.id || null);
        // Optionally clear it so it doesn't persist
        // sessionStorage.removeItem('selected_offer');
      }
    } catch (err) {
      // ignore
    }
  }, []);

  // Timer countdown
  useEffect(() => {
    if (loading && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [loading, timer]);

  const handleComplete = React.useCallback(() => {
    setIsUnlocked(true);
    setLoading(false);
    // Auto-redirect after showing success (navigate to the offer page instead of external site)
    setTimeout(() => {
      // Use hash navigation to support HashRouter
      window.location.hash = `#/offer/${encodeURIComponent(String(offer.id))}`;
    }, 3000);
  }, [offer.id]);

  // Progress simulation
  useEffect(() => {
    if (loading) {
      // Trigger completion after 5 seconds for simulation
      const timeout = setTimeout(() => {
        handleComplete();
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [loading, handleComplete]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const shimmerVariants = {
    initial: { x: "-100%" },
    animate: {
      x: "100%",
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: "linear",
      },
    },
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-b from-gray-900 to-black px-4 py-6 pb-24 max-w-4xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <button
          onClick={() => navigate(-1)}
          className="group flex items-center gap-2 text-gray-400 mb-8 hover:text-white transition-colors"
        >
          <motion.div
            whileHover={{ x: -5 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.div>
          <span className="text-sm font-medium">Back to Offers</span>
        </button>
      </motion.div>

      {sessionOfferName && (
        <div className="mb-4 p-3 rounded-lg bg-green-900/20 border border-green-900/30 text-green-200 text-sm">
          You just completed: <strong className="text-white">{sessionOfferName}</strong>
        </div>
      )}

      <motion.div
        className="space-y-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Main Card */}
        <motion.div
          variants={itemVariants}
          className="relative bg-gradient-to-br from-gray-900/90 to-black/90 rounded-3xl overflow-hidden border border-gray-800/50 shadow-2xl backdrop-blur-sm"
        >
          {/* Animated border */}
          <motion.div
            className="absolute inset-0 rounded-3xl p-[1px]"
            style={{
              background: "linear-gradient(45deg, #8b5cf6, #ec4899, #3b82f6)",
            }}
            animate={{
              background: [
                "linear-gradient(45deg, #8b5cf6, #ec4899, #3b82f6)",
                "linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899)",
                "linear-gradient(45deg, #ec4899, #3b82f6, #8b5cf6)",
              ],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <div className="absolute inset-[1px] bg-gray-900 rounded-3xl" />
          </motion.div>

          {/* Reward Preview */}
          <div className="relative z-10 p-8">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              {/* Image Container */}
              <motion.div
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="relative w-48 h-48 rounded-2xl overflow-hidden border-2 border-white/10"
              >
                <img
                  src={offer.image}
                  alt={offer.title}
                  className="w-full h-full object-cover"
                />

                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  variants={shimmerVariants}
                  initial="initial"
                  animate="animate"
                />

                {/* Lock/Unlock indicator */}
                <motion.div
                  className="absolute top-4 right-4"
                  animate={{
                    y: [0, -5, 0],
                    transition: {
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    },
                  }}
                >
                  {isUnlocked ? (
                    <Unlock className="w-6 h-6 text-green-500" />
                  ) : (
                    <Lock className="w-6 h-6 text-yellow-500" />
                  )}
                </motion.div>
              </motion.div>

              {/* Content */}
              <div className="flex-1 space-y-6">
                <div>
                  <motion.div
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 mb-4"
                    whileHover={{ scale: 1.05 }}
                  >
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm font-medium text-white">
                      ACTIVE OFFER
                    </span>
                  </motion.div>

                  <motion.h1
                    className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent"
                    variants={itemVariants}
                  >
                    {offer.title}
                  </motion.h1>

                  <motion.p
                    className="text-gray-300 text-lg leading-relaxed"
                    variants={itemVariants}
                  >
                    {offer.subtitle}
                  </motion.p>
                </div>

                {/* Reward Stats */}
                <motion.div
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                  variants={containerVariants}
                >
                  {[
                    {
                      icon: Gift,
                      label: "Reward",
                      value: offer.rewardValue,
                      color: "text-green-400",
                    },
                    {
                      icon: Clock,
                      label: "Time",
                      value: offer.estimatedTime,
                      color: "text-blue-400",
                    },
                    {
                      icon: Shield,
                      label: "Difficulty",
                      value: offer.difficulty,
                      color: "text-yellow-400",
                    },
                    {
                      icon: Sparkles,
                      label: "Type",
                      value: offer.rewardType,
                      color: "text-purple-400",
                    },
                  ].map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      variants={itemVariants}
                      className="bg-gray-800/50 p-4 rounded-xl border border-gray-700/50"
                      whileHover={{ y: -4 }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <stat.icon className={`w-4 h-4 ${stat.color}`} />
                        <span className="text-xs text-gray-400">
                          {stat.label}
                        </span>
                      </div>
                      <div className={`text-lg font-bold ${stat.color}`}>
                        {stat.value}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

              {/* Locker-based Unlock */}
        <motion.div variants={itemVariants}>
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Unlock Your Reward
              </h2>

              {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>
                    Time remaining: {Math.floor(timer / 60)}:
                    {String(timer % 60).padStart(2, "0")}
                  </span>
                </div>
              )}
            </div>

            <div className="mb-4 text-sm text-gray-300">Use the locker to complete an offer and claim your reward. When finished, press "I've completed" inside the locker modal to continue.</div>

            <div className="mt-4">
              <LockerButton offer={offer}>
                <div className="w-full text-center py-4 rounded-2xl font-bold text-sm lg:text-base transition-all duration-300 bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg">Open Locker</div>
              </LockerButton>
            </div>

            {/* If a session offer was just completed, show a quick CTA */}
            {sessionOfferName && (
              <div className="mt-4 p-3 rounded-md bg-green-900/20 border border-green-900/30 text-green-200 text-sm">
                You just completed: <strong className="text-white">{sessionOfferName}</strong>
              </div>
            )}
          </div>
        </motion.div>

        {/* Security & Disclaimer */}
        <motion.div variants={itemVariants}>
          <div className="space-y-4">
            <motion.div
              className="flex items-center gap-2 text-green-400 font-bold text-sm uppercase tracking-wider"
              whileHover={{ scale: 1.02 }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <ShieldCheck className="w-5 h-5" />
              </motion.div>
              <span>Verified & Secured by FreeBonusNow</span>
            </motion.div>

            <motion.div
              className="bg-black/40 p-4 rounded-xl border border-gray-800"
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 leading-relaxed mb-2">
                    <strong className="text-gray-400">Disclaimer:</strong>{" "}
                    FreeBonusNow is a content discovery platform. All offers,
                    brands and trademarks are the property of their respective
                    owners. No hacks, cracks or illegal software provided.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {[
                      "Secure Connection",
                      "Privacy Protected",
                      "No Hidden Fees",
                      "Instant Rewards",
                    ].map((tag, i) => (
                      <motion.span
                        key={tag}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.1 }}
                        className="px-3 py-1 bg-gray-800/50 rounded-full text-xs text-gray-400"
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Floating particles */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            initial={{
              x: Math.random() * 100 + "vw",
              y: Math.random() * 100 + "vh",
              opacity: 0.1,
            }}
            animate={{
              y: [null, "-100vh"],
              opacity: [0.1, 0.5, 0.1],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "linear",
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default OfferBridge;
